/**
 * Locale-aware formatting.
 *
 * Everything a user reads as a number, a date or a currency goes through here, because "1,234.56"
 * and "1.234,56" are the same number and only one of them is right for a given reader. The brief
 * also lists what must NOT be localised — repository names, branches, task ids, technical
 * identifiers — and those simply never reach these functions.
 */

import type { Locale } from './i18n'

export function formatNumber(value: number, locale: Locale, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(locale, options).format(value)
}

export function formatCompact(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

/**
 * Money, always in USD.
 *
 * The currency is not localised because it is not a presentation choice: providers bill in dollars
 * and the number is a dollar amount. A Brazilian reader sees "US$ 2,84" — the separators follow
 * their locale, the currency does not, because converting it would invent an exchange rate.
 */
export function formatCost(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined) return '—'

  // Sub-cent amounts are common per-session and would all render as $0.00.
  const digits = value > 0 && value < 0.01 ? 4 : 2

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  }).format(value)
}

export function formatPercent(value: number, locale: Locale): string {
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)}%`
}

export function formatDate(value: string | Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date)
}

export function formatDateTime(value: string | Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

/** Timeline rows: time only, since the date is the section heading. */
export function formatTime(value: string | Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(date)
}

/**
 * Durations, as an engineer would say them: 42m, 2h 14m, 3d 4h.
 *
 * Deliberately not Intl.RelativeTimeFormat — that produces "in 2 hours" and "3 days ago", which is
 * the wrong shape for a column of elapsed times that has to align and stay narrow.
 */
export function formatDuration(seconds: number, locale: Locale): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—'

  const s = Math.floor(seconds)
  if (s < 60) return `${s}s`

  const minutes = Math.floor(s / 60)
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours < 24) return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`

  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24

  // The unit letters are English abbreviations and stay that way: they are read as symbols, and
  // "2d 4h" is understood by a Brazilian engineer in a way a translated "2d 4h" would not improve.
  void locale
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

/** "3m ago", for freshness indicators where the exact instant does not matter. */
export function formatRelative(value: string | Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value
  const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const absolute = Math.abs(deltaSeconds)

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' })

  if (absolute < 60) return formatter.format(deltaSeconds, 'second')
  if (absolute < 3600) return formatter.format(Math.round(deltaSeconds / 60), 'minute')
  if (absolute < 86400) return formatter.format(Math.round(deltaSeconds / 3600), 'hour')
  return formatter.format(Math.round(deltaSeconds / 86400), 'day')
}

/** Initials for an avatar, from a display name. Two characters, upper case. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/**
 * A stable colour for a name, when no avatar colour was assigned.
 *
 * Deterministic so a person does not change colour between page loads, which would make the floor
 * unreadable at a glance — colour is how you find someone before you have read their name.
 */
const PALETTE = ['#4C7DF0', '#12A594', '#E5484D', '#F76B15', '#8E4EC6', '#0090FF', '#46A758', '#D6409F']

export function colorFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  return PALETTE[Math.abs(hash) % PALETTE.length]!
}
